import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import JoditEditor from 'jodit-react';

type ButtonPreset = 'default' | 'limited-text-only';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  height?: number;
  className?: string;
  error?: boolean;
  previewTemplate?: string;
  buttonPreset?: ButtonPreset;
}

export function RichTextEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  height = 300,
  className = '',
  error = false,
  previewTemplate,
  buttonPreset = 'default'
}: RichTextEditorProps) {
  const editor = useRef(null);

  const buttonConfigs = {
    'default': 'bold,italic,underline,strikethrough,eraser,ul,ol,indent,outdent,left,right,center,font,fontsize,paragraph,image,paste,selectall,hr,table,link,undo,redo,find,preview',
    'limited-text-only': 'bold,italic,underline,ul,ol,indent,outdent,align,font,fontsize,hr,link,preview'
  };

  const previewTemplateRef = useRef(previewTemplate);
  useEffect(() => {
    previewTemplateRef.current = previewTemplate;
  }, [previewTemplate]);

  const handlePreview = useCallback((joditInstance: any) => {
    const template = previewTemplateRef.current;
    if (!template) return;

    const html = joditInstance.value || '';
    const fullHtml = template.replace('{{CONTENT}}', html);
    
    const width = 800;
    const height = 600;
    
    // Calculate position to center over the current window
    const windowX = window.screenX !== undefined ? window.screenX : window.screenLeft;
    const windowY = window.screenY !== undefined ? window.screenY : window.screenTop;
    const windowWidth = window.outerWidth || window.innerWidth;
    const windowHeight = window.outerHeight || window.innerHeight;

    const left = windowX + (windowWidth - width) / 2;
    const top = windowY + (windowHeight - height) / 2;
    
    const previewWindow = window.open('', '_blank', `width=${width},height=${height},left=${left},top=${top}`);
    if (previewWindow) {
      previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Preview</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
            </style>
          </head>
          <body>${fullHtml}</body>
        </html>
      `);
      previewWindow.document.close();
    }
  }, []);

  const config = useMemo(() => {
    let buttons = buttonConfigs[buttonPreset].split(',');
    
    const configObj: any = {
      readonly: false,
      height: height,
      placeholder: placeholder || 'Start typing...',
      uploader: {
        insertImageAsBase64URI: true
      },
      buttons: buttons,
      showXPathInStatusbar: false,
      showCharsCounter: false,
      showWordsCounter: false,
      toolbarAdaptive: false,
      toolbarSticky: false,
      theme: 'default',
      addNewLine: false,
      editorCssClass: error ? 'jodit-error' : '',
      style: {
        border: error ? '1px solid #ef4444' : '1px solid #d1d5db',
        borderRadius: '0.375rem'
      }
    };

    return configObj;
  }, [height, placeholder, error, buttonPreset]);

  // Setup preview button customization when editor is ready
  const setupPreviewButton = useCallback((joditInstance: any) => {
    if (!previewTemplateRef.current || !joditInstance) return;

    // Remove any existing beforeCommand listeners for preview
    // Note: Try-catch and defensive checks are necessary for development scenarios (hot reload)
    // where the editor instance may be partially destroyed or in an inconsistent state
    try {
      if (joditInstance.__customPreviewHandler && joditInstance.events && typeof joditInstance.events.off === 'function') {
        joditInstance.events.off('beforeCommand', joditInstance.__customPreviewHandler);
      }
    } catch (e) {
      // Ignore errors during cleanup - editor may be in a destroyed state during hot reload
    }

    // Create and register the preview command handler
    const previewHandler = (command: string) => {
      if (command === 'preview') {
        handlePreview(joditInstance);
        return false; // Prevent default preview
      }
    };

    // Store reference to the handler so we can remove it later
    joditInstance.__customPreviewHandler = previewHandler;
    
    // Listen for the preview command before it executes
    // Note: Try-catch and defensive checks are necessary for development scenarios (hot reload)
    try {
      if (joditInstance.events && typeof joditInstance.events.on === 'function') {
        joditInstance.events.on('beforeCommand', previewHandler);
      }
    } catch (e) {
      // Ignore errors during setup - editor may be in a destroyed state during hot reload
    }
  }, [handlePreview]);

  // Intercept preview button click/command if previewTemplate is provided
  useEffect(() => {
    if (!previewTemplate) return;

    const timer = setTimeout(() => {
      const editorElement = editor.current as any;
      if (!editorElement) return;
      
      const joditInstance = editorElement.jodit || editorElement;
      if (!joditInstance) return;

      setupPreviewButton(joditInstance);
    }, 200);

    return () => {
      clearTimeout(timer);
      // Cleanup on unmount
      // Note: Try-catch and defensive checks are necessary for development scenarios (hot reload)
      // where the editor instance may be partially destroyed or in an inconsistent state
      try {
        const editorElement = editor.current as any;
        if (editorElement) {
          const joditInstance = editorElement.jodit || editorElement;
          if (joditInstance && joditInstance.__customPreviewHandler && joditInstance.events && typeof joditInstance.events.off === 'function') {
            joditInstance.events.off('beforeCommand', joditInstance.__customPreviewHandler);
          }
        }
      } catch (e) {
        // Ignore errors during cleanup - editor may be in a destroyed state during hot reload
      }
    };
  }, [previewTemplate, value, setupPreviewButton]);

  const handleBlur = useCallback((newValue: string) => {
    if (onBlur) {
      onBlur(newValue);
    }
  }, [onBlur]);

  const handleChange = useCallback((newValue: string) => {
    onChange(newValue);
  }, [onChange]);

  return (
    <div className={`rich-text-editor-wrapper ${className}`}>
      <style>{`
        .rich-text-editor-wrapper .jodit-container {
          border-radius: 0.375rem;
        }
        .rich-text-editor-wrapper .jodit-container.jodit-error {
          border-color: #ef4444;
        }
        .rich-text-editor-wrapper .jodit-container:focus-within {
          border-color: #3b82f6;
          outline: 2px solid transparent;
          outline-offset: 2px;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
        .rich-text-editor-wrapper .jodit-toolbar-editor-collection {
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
        }
        .rich-text-editor-wrapper .jodit-wysiwyg {
          min-height: ${height - 40}px;
          padding: 0.75rem;
        }
        .rich-text-editor-wrapper .jodit-toolbar-button[data-name="preview"] {
          min-width: auto;
        }
        .rich-text-editor-wrapper .jodit-toolbar-button[data-name="preview"] .jodit-icon,
        .rich-text-editor-wrapper .jodit-toolbar-button[data-name="preview"] svg {
          display: none !important;
        }
        .rich-text-editor-wrapper .jodit-toolbar-button[data-name="preview"] .preview-text {
          display: inline-block;
        }
      `}</style>
      <JoditEditor
        ref={editor}
        value={value}
        config={config}
        onBlur={handleBlur}
        onChange={handleChange}
        onReady={(jodit) => {
          if (previewTemplate) {
            setupPreviewButton(jodit);
          }
        }}
      />
    </div>
  );
}
